import { useCallback, useEffect, useState } from 'react'
import { CalendarClock, Loader2, Plus, Save, Trash2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { explain } from '@/shared/api/problem'
import { MetricCard } from '@/components/erp/primitives'
import { ChartFrame, RankedBars } from '@/shared/ui/charts'
import { ErrorStrip, LoadingBlock, Note } from '@/shared/ui/states'
import { fmtDateTime, fmtNumber } from '@/shared/lib/format'
import { useCan } from '@/shared/auth/session'
import {
  deleteSchedule, getQuality, getUsage, listPlaybooks, listSchedules, saveSchedule, setBudget,
  type Playbook, type Schedule, type Usage,
} from './api'
import { kes, usd } from './vocab'

const CADENCES = ['DAILY', 'WEEKDAYS', 'WEEKLY', 'MONTHLY']

/**
 * What this costs, what it is spending it on, and what arrives without being asked.
 *
 * ## Cost is stored in USD and converted only here
 *
 * Providers bill in dollars, so the ledger holds dollars. The KES figure is
 * computed from a setting at render time and never written to a row — a stored
 * conversion silently re-prices last October every time the rate moves, and then
 * the ledger and the invoice disagree for a reason nobody can reconstruct.
 *
 * ## The quality number is the one worth watching
 *
 * “Charts requested that did not resolve” counts the times an editor named a
 * dataset the brief did not contain. It climbs days before anybody notices the
 * prose getting worse, and it is the reason those visuals are kept as rows
 * instead of being dropped.
 */
export function UsagePage() {
  const [usage, setUsage] = useState<Usage | null>(null)
  const [quality, setQuality] = useState<{ unboundVisuals: number; totalRuns: number } | null>(null)
  const [schedules, setSchedules] = useState<Schedule[]>([])
  const [playbooks, setPlaybooks] = useState<Playbook[]>([])
  const [error, setError] = useState<string | null>(null)
  const [days, setDays] = useState(30)
  const [busy, setBusy] = useState(false)
  const mayConfigure = useCan('advisor:configure')

  const load = useCallback(async () => {
    try {
      setError(null)
      const [u, q, s, p] = await Promise.all([
        getUsage(days), getQuality(days), listSchedules(), listPlaybooks(),
      ])
      setUsage(u)
      setQuality(q)
      setSchedules(s)
      setPlaybooks(p)
    } catch (err) {
      setError(explain(err))
    }
  }, [days])

  useEffect(() => { void load() }, [load])

  const saveCap = async (capUsd: number, hardStop: boolean) => {
    setBusy(true)
    try {
      await setBudget({ capUsd, hardStop })
      await load()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-erp-lg font-semibold">Usage</h1>
          <p className="text-erp-xs text-muted-foreground mt-0.5">
            Every model call is also an {`integration_call`} — the same ledger as SMS and every
            other third party — so this and the Integrations screen agree by construction.
          </p>
        </div>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))}
          className="erp-input h-9 text-erp-sm">
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      <ErrorStrip message={error} className="rounded-md border" />
      {usage === null && !error && <LoadingBlock />}

      {usage && (
        <>
          <div className="grid gap-2"
            style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
            <MetricCard label="Spent this month" value={usd(usage.budget.spentUsd, 2)}
              sub={kes(usage.budget.spentUsd, usage.budget.usdToKes)} />
            <MetricCard label="Monthly cap" value={usd(usage.budget.capUsd, 2)}
              sub={usage.budget.hardStop ? 'runs are refused over it' : 'runs pass, an alert fires'} />
            <MetricCard label="Runs" value={fmtNumber(usage.totalRuns)}
              sub={`${fmtNumber(usage.failedRuns)} failed or budget-stopped`} />
            <MetricCard label="Charts that did not resolve"
              value={fmtNumber(quality?.unboundVisuals ?? 0)}
              sub="rising means a prompt has drifted" />
          </div>

          {usage.budget.alertedAt && (
            <Note tone="info">
              The {usage.budget.alertAtPct}% alert fired on{' '}
              {fmtDateTime(usage.budget.alertedAt)}.
            </Note>
          )}

          <div className="grid gap-4 md:grid-cols-2">
            <ChartFrame title="By vendor" description="Which account to renegotiate">
              <RankedBars points={usage.byVendor.map((r) => ({
                label: r.label, value: Number(r.costUsd),
              }))} formatValue={(n) => usd(n)} />
            </ChartFrame>
            <ChartFrame title="By model" description="Whether the router is choosing sensibly">
              <RankedBars points={usage.byModel.slice(0, 10).map((r) => ({
                label: r.label, value: Number(r.costUsd),
              }))} formatValue={(n) => usd(n)} />
            </ChartFrame>
            <ChartFrame title="By playbook" description="What this is actually used for">
              <RankedBars points={usage.byPlaybook.map((r) => ({
                label: r.label, value: Number(r.costUsd),
              }))} formatValue={(n) => usd(n)} />
            </ChartFrame>
            <ChartFrame title="By day" description="Whether something ran away overnight">
              <RankedBars points={usage.byDay.map((r) => ({
                label: r.label, value: Number(r.costUsd),
              }))} formatValue={(n) => usd(n)} />
            </ChartFrame>
          </div>

          {mayConfigure && (
            <BudgetForm budget={usage.budget} busy={busy} onSave={saveCap} />
          )}

          <ScheduleSection
            schedules={schedules}
            playbooks={playbooks}
            mayConfigure={mayConfigure}
            onChanged={() => void load()} />
        </>
      )}
    </div>
  )
}

function BudgetForm({ budget, busy, onSave }: {
  budget: Usage['budget']
  busy: boolean
  onSave: (capUsd: number, hardStop: boolean) => Promise<void>
}) {
  const [cap, setCap] = useState(String(budget.capUsd))
  const [hardStop, setHardStop] = useState(budget.hardStop)

  return (
    <section className="rounded-md border border-border p-3 space-y-2">
      <h2 className="text-erp-sm font-medium">This month&rsquo;s ceiling</h2>
      <p className="text-erp-xs text-muted-foreground">
        Hard stop refuses a run that would cross the cap. Off, it lets the run through and
        raises an alert — election week is exactly when somebody would rather overspend than go
        blind, so this is a deployment&rsquo;s call rather than a rule.
      </p>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="text-erp-xs text-muted-foreground">Cap (USD)</label>
          <input value={cap} onChange={(e) => setCap(e.target.value)} inputMode="decimal"
            className="erp-input w-32 text-erp-sm" />
        </div>
        <label className="flex items-center gap-1.5 text-erp-sm">
          <input type="checkbox" checked={hardStop}
            onChange={(e) => setHardStop(e.target.checked)} />
          Refuse runs over the cap
        </label>
        <button type="button" disabled={busy}
          onClick={() => void onSave(Number(cap), hardStop)}
          className="erp-btn h-9 px-3 text-erp-sm inline-flex items-center gap-2">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <Save className="h-3.5 w-3.5" />}
          Save
        </button>
      </div>
    </section>
  )
}

/** Scheduled briefings — the reason this module gets opened at all. */
function ScheduleSection({ schedules, playbooks, mayConfigure, onChanged }: {
  schedules: Schedule[]
  playbooks: Playbook[]
  mayConfigure: boolean
  onChanged: () => void
}) {
  const [adding, setAdding] = useState(false)
  const [playbookCode, setPlaybookCode] = useState('MORNING_BRIEF')
  const [cadence, setCadence] = useState('WEEKDAYS')
  const [hour, setHour] = useState(6)
  const [pin, setPin] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const create = async () => {
    setBusy(true)
    setError(null)
    try {
      await saveSchedule({
        playbookCode,
        title: playbooks.find((p) => p.code === playbookCode)?.title ?? playbookCode,
        cadence,
        hourLocal: hour,
        pinReport: pin,
        enabled: true,
      })
      setAdding(false)
      onChanged()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h2 className="text-erp-sm font-medium">Scheduled briefings</h2>
          <p className="text-erp-xs text-muted-foreground">
            An advisory console nobody opens is worth nothing. A pinned schedule makes the
            answer arrive instead of waiting.
          </p>
        </div>
        {mayConfigure && !adding && (
          <button type="button" onClick={() => setAdding(true)}
            className="erp-btn h-8 px-2.5 text-erp-xs inline-flex items-center gap-1.5">
            <Plus className="h-3.5 w-3.5" /> Add
          </button>
        )}
      </div>

      <ErrorStrip message={error} className="rounded-md border" />

      {adding && (
        <div className="rounded-md border border-border p-3 space-y-2">
          <div className="grid gap-2 md:grid-cols-4">
            <label className="text-erp-xs text-muted-foreground">
              Playbook
              <select value={playbookCode} onChange={(e) => setPlaybookCode(e.target.value)}
                className="erp-input w-full text-erp-sm">
                {playbooks.map((p) => (
                  <option key={p.code} value={p.code}>{p.title}</option>
                ))}
              </select>
            </label>
            <label className="text-erp-xs text-muted-foreground">
              Cadence
              <select value={cadence} onChange={(e) => setCadence(e.target.value)}
                className="erp-input w-full text-erp-sm">
                {CADENCES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label className="text-erp-xs text-muted-foreground">
              Hour (EAT)
              <input type="number" min={0} max={23} value={hour}
                onChange={(e) => setHour(Number(e.target.value))}
                className="erp-input w-full text-erp-sm" />
            </label>
            <label className="flex items-end gap-1.5 text-erp-sm">
              <input type="checkbox" checked={pin} onChange={(e) => setPin(e.target.checked)} />
              Pin the result
            </label>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setAdding(false)}
              className="erp-btn h-8 px-2.5 text-erp-xs">Cancel</button>
            <button type="button" disabled={busy} onClick={() => void create()}
              className="erp-btn erp-btn-primary h-8 px-2.5 text-erp-xs">
              {busy ? 'Saving…' : 'Schedule it'}
            </button>
          </div>
        </div>
      )}

      {schedules.length === 0 ? (
        <p className="text-erp-xs text-muted-foreground">Nothing is scheduled.</p>
      ) : (
        <ul className="space-y-1">
          {schedules.map((schedule) => (
            <li key={schedule.id}
              className={cn('flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border',
                'border-border px-2.5 py-1.5', !schedule.enabled && 'opacity-60')}>
              <CalendarClock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <span className="text-erp-sm min-w-0 flex-1 truncate">{schedule.title}</span>
              <span className="text-erp-xs text-muted-foreground">
                {schedule.cadence.toLowerCase()} at {String(schedule.hourLocal).padStart(2, '0')}:00
              </span>
              <span className="text-erp-xs text-muted-foreground">
                next {schedule.nextRunAt ? fmtDateTime(schedule.nextRunAt) : '—'}
              </span>
              {schedule.pinReport && (
                <span className="text-erp-xs text-chocolate">pins its report</span>
              )}
              {mayConfigure && (
                <button type="button"
                  onClick={() => void deleteSchedule(schedule.id).then(onChanged)}
                  className="erp-btn h-7 px-2 text-erp-xs inline-flex items-center gap-1">
                  <Trash2 className="h-3 w-3" /> Remove
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
